// Explorer > one person: who they are, plus every project they own.
//
// The project list is the reason this view exists — from a person you reach
// their work in one click.
//
// Two data sources, one set of markup (see lib/explorer-access.ts):
//   full    — /api/admin/users/{id}: identity record + cross-project agent notes
//             + per-project content tallies.
//   support — /api/admin/support/people/{id}: the narrow, talent-free payload
//             that Customer Support's own person page used to read.
// Both normalize to `UserDetailView`'s props, so the two modes cannot drift into
// two different-looking pages.
import {
  Card,
  SortableTh,
  TableExport,
  TableFilters,
  type FilterColumn,
  useColumnFilters,
  usePagedRows,
  useTableSort,
} from "@recruiter/shared";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo } from "react";

import { AlertCell, alertSortValue } from "../components/alert-cell";
import { AreaBoundary } from "../components/area-gate";
import { BackLink } from "../components/back-link";
import { ArrowRightIcon } from "../components/icons";
import { HandlingChips } from "../components/handling-chips";
import { ProjectStageBadge } from "../components/project-stage-badge";
import { SellerIntentTag } from "../components/seller-intent-tag";
import { StarToggle } from "../components/star-toggle";
import { WorkShapeTag } from "../components/work-shape-tag";
import { ModelTierTag } from "../components/model-tier-tag";
import { EmptyState, LoadingState } from "../components/states";
import { BlockedBadge, UserAdminActions } from "../components/user-admin-actions";
import { useExplorerMode } from "../lib/explorer-access";
import {
  budgetSortValue,
  formatBudget,
  formatDateTime,
  formatTimeline,
  type ProjectBudget,
  type ProjectTimeline,
} from "../lib/format";
import {
  HANDLING_KEYS,
  type HandlingSource,
  handlingFilterValue,
  handlingLabel,
} from "../lib/project-handling";
import { PROJECT_STAGES, stageDisplay, stageSortValue } from "../lib/project-stage";
import { projectTitle } from "../lib/project-title";
import { WORK_SHAPES, workShapeLabel } from "../lib/work-shape";
import { explorerUserQuery, supportPersonQuery } from "../lib/queries";
import { ShowMoreRow } from "../components/show-more-row";
import "./explorer.css";

interface OwnedProject {
  id: string;
  name: string;
  summary: string | null;
  parentProjectId: string | null;
  kind: string | null;
  /** The engagement SHAPE the agents latched (project / ongoing / hybrid) —
   *  the work-shape tag + filter. Null = not classified (or a payload that does
   *  not carry it, support mode). */
  workShape: string | null;
  /** How far the project got — the same ladder the Explorer's table badges, so a
   *  project cannot read one rung on its owner's page and another on the list. */
  stage: string | null;
  /** What the client committed to spend and by when. Null = the payload does not
   *  carry them (support mode), which the table hides rather than dashing out. */
  budget: ProjectBudget | null;
  timeline: ProjectTimeline | null;
  /** The CLIENT's own messages, not the transcript length. */
  messages: number;
  /** Null in support mode, which does not carry file counts. */
  files: number | null;
  updatedAt: string | null;
  alertCount: number;
  alertReasons: string[];
  /** Talent pitching their services, not a client buying them. A durable
   *  tag, so it stays after the matching alert is resolved. */
  sellerIntent: boolean;
  /** Who is on this project RIGHT NOW (`lib/project-handling.ts`). Empty in
   *  support mode, whose payload does not carry it. */
  handling: HandlingSource;
  /** Starred by the CALLING operator. Null in support mode, which has no star. */
  starred: boolean | null;
}

interface DetailUser {
  id: string;
  email: string;
  displayName: string | null;
  agentNotes: string | null;
  blocked: boolean;
  /** The regional model tier the account was first seen on (luna / terra,
   *  migration 0195) and the country it was decided from. Absent in support
   *  mode, whose payload does not carry them. */
  modelTier?: string | null;
  originCountry?: string | null;
  /** Their Fiverr inbox conversation, built backend-side so it points at the
   *  right marketplace for this environment. Null when we never learned their
   *  handle (a guest, or a sign-in that carried none) — and then the button is
   *  simply absent, rather than a link into an inbox that is not theirs. */
  fiverrInboxUrl?: string | null;
}

const PROJECT_SORT = {
  // Sort on the DISPLAYED title, not the raw (often blank) name.
  project: (p: OwnedProject) => projectTitle(p),
  // Ladder position, not the label: alphabetical would put "brief" above
  // "search" and make the column useless for reading the funnel.
  stage: (p: OwnedProject) => stageSortValue(p.stage),
  kind: (p: OwnedProject) => p.kind,
  budget: (p: OwnedProject) => budgetSortValue(p.budget),
  timeline: (p: OwnedProject) => p.timeline?.deliveryMaxDays ?? null,
  alerts: (p: OwnedProject) => alertSortValue(p.alertCount, p.alertReasons),
  messages: (p: OwnedProject) => p.messages,
  files: (p: OwnedProject) => p.files,
  updated: (p: OwnedProject) => p.updatedAt,
} as const;

/** The same three categorical columns the Explorer's project table filters on,
 *  declared here rather than shared because the row shape differs (this payload
 *  has file counts and no owner). One person can easily own thirty projects, and
 *  "which of theirs is stuck at brief" is the question this page is opened with.
 *
 *  Each control hides itself when the rows only hold one value, so a person with
 *  two projects gets no filter strip at all. */
const PROJECT_FILTERS: readonly FilterColumn<OwnedProject>[] = [
  {
    key: "stage",
    label: "Filter by stage",
    allLabel: "All stages",
    // Null IS "new" for the badge and the sort, so the filter agrees.
    value: (p) => p.stage ?? "new",
    optionLabel: (value) => stageDisplay(value).label,
    order: PROJECT_STAGES,
  },
  {
    key: "kind",
    label: "Filter by kind",
    allLabel: "All kinds",
    value: (p) => p.kind ?? "project",
  },
  {
    key: "workShape",
    label: "Filter by work shape",
    allLabel: "All work shapes",
    value: (p) => p.workShape,
    optionLabel: (value) => workShapeLabel(value),
    noValueLabel: workShapeLabel("none"),
    order: WORK_SHAPES,
  },
  {
    key: "handling",
    label: "Filter by handling",
    allLabel: "Anyone",
    value: (p) => handlingFilterValue(p.handling),
    optionLabel: handlingLabel,
    noValueLabel: "Mira only",
    order: HANDLING_KEYS,
  },
  {
    key: "alerts",
    label: "Filter by alert",
    allLabel: "Any alerts",
    // On the REASONS, not the count: a flagged project and a flagged project
    // are different jobs depending on why. A flag whose reason went unrecorded
    // keeps its own bucket rather than landing in "No alerts", which would be
    // the filter saying something untrue about a project that IS flagged.
    value: (p) =>
      p.alertCount === 0
        ? null
        : p.alertReasons.length > 0
          ? p.alertReasons
          : ["(reason not recorded)"],
    optionLabel: (value) => value.replaceAll("_", " "),
    noValueLabel: "No alerts",
    order: ["agent_error", "chat_misuse", "human_requested", "client_waiting", "frustration"],
  },
];

/** Render the buyer-identity record as labelled rows.
 *
 *  Deliberately generic rather than field-by-field typed: this is an audit view,
 *  and a field the backend adds tomorrow should appear here without a frontend
 *  release. Objects are JSON-stringified rather than dropped, so nothing is
 *  silently hidden. */
function IdentityFacts({ identity }: { identity: Record<string, unknown> | null }) {
  const entries = Object.entries(identity ?? {}).filter(
    ([, value]) => value !== null && value !== undefined && value !== "",
  );
  if (entries.length === 0) {
    return <p className="ex-muted">Nothing captured yet.</p>;
  }
  return (
    <dl className="ex-facts">
      {entries.map(([key, value]) => (
        <div key={key}>
          <dt>{key.replaceAll("_", " ")}</dt>
          <dd>{typeof value === "object" ? JSON.stringify(value) : String(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

function OwnedProjects({ projects }: { projects: OwnedProject[] }) {
  // This list is itself a jumping-off point (a row opens a project), so it needs
  // the same restore the Explorer's own tables have.
  const filters = useColumnFilters(projects, PROJECT_FILTERS, {
    restore: "explorer-user/projects/filters",
  });
  const sorted = useTableSort(filters.rows, PROJECT_SORT, null, {
    restore: "explorer-user/projects/sort",
  });
  const paged = usePagedRows(sorted.rows, { restore: "explorer-user/projects/window" });
  const showFiles = projects.some((p) => p.files !== null);
  // Support mode's payload carries neither, so the columns are absent there
  // rather than a wall of dashes — same rule the Files column follows.
  const showBudget = projects.some((p) => p.budget !== null);
  // Same rule again for the star: support mode has no route to write one.
  const showStar = projects.some((p) => p.starred !== null);

  if (projects.length === 0) {
    return <EmptyState title="No projects yet" message="This person hasn't started a project." />;
  }

  // Kept above the empty state too: a filter that emptied the table has to stay
  // reachable, or the only way back is a reload.
  const filterBar =
    filters.controls.length > 0 ? (
      <div className="ex-list-filter">
        <span className="ex-list-filter__label">Show:</span>
        <TableFilters controls={filters.controls} active={filters.active} onClear={filters.clear} />
      </div>
    ) : null;

  if (filters.rows.length === 0) {
    return (
      <>
        {filterBar}
        <EmptyState title="No projects match those filters" />
      </>
    );
  }

  return (
    <>
      {filterBar}
      <div className="rc-table-wrap">
        <table className="rc-table">
          <TableExport name="user-projects" label="this user's projects" />
          <thead>
            <tr>
              {showStar ? (
                <th className="ex-star-col">
                  <span className="ad-visually-hidden">Starred</span>
                </th>
              ) : null}
              <SortableTh sortKey="project" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                Project
              </SortableTh>
              <SortableTh sortKey="stage" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                Stage
              </SortableTh>
              <SortableTh sortKey="kind" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                Kind
              </SortableTh>
              {showBudget ? (
                <>
                  <SortableTh
                    sortKey="budget"
                    numeric
                    ariaSort={sorted.ariaSort}
                    onToggle={sorted.toggle}
                  >
                    Budget
                  </SortableTh>
                  <SortableTh
                    sortKey="timeline"
                    numeric
                    ariaSort={sorted.ariaSort}
                    onToggle={sorted.toggle}
                  >
                    Timeline
                  </SortableTh>
                </>
              ) : null}
              <SortableTh sortKey="alerts" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                Alerts
              </SortableTh>
              {/* Named for what it counts — the client's own messages, not the
                whole transcript. Same column as the Explorer's table. */}
              <SortableTh
                sortKey="messages"
                numeric
                ariaSort={sorted.ariaSort}
                onToggle={sorted.toggle}
              >
                Client msgs
              </SortableTh>
              {showFiles ? (
                <SortableTh
                  sortKey="files"
                  numeric
                  ariaSort={sorted.ariaSort}
                  onToggle={sorted.toggle}
                >
                  Files
                </SortableTh>
              ) : null}
              <SortableTh sortKey="updated" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                Updated
              </SortableTh>
              <th>
                <span className="ad-visually-hidden">Open</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {paged.shown.map((project) => (
              <tr key={project.id}>
                {showStar ? (
                  <td className="ex-star-col">
                    <StarToggle
                      projectId={project.id}
                      starred={project.starred ?? false}
                      title={projectTitle(project)}
                    />
                  </td>
                ) : null}
                <td>
                  <span className="ex-title-line">
                    <Link
                      to="/explorer/projects/$projectId"
                      params={{ projectId: project.id }}
                      className="ex-primary-link"
                    >
                      {projectTitle(project)}
                    </Link>
                    <SellerIntentTag sellerIntent={project.sellerIntent} />
                  </span>
                  {/* A child task shows its parentage rather than reading as a
                    stray top-level project. */}
                  {project.parentProjectId ? (
                    <span className="rc-cell-sub">Task under another project</span>
                  ) : null}
                </td>
                <td>
                  <ProjectStageBadge stage={project.stage} />
                  {/* Who is on it now, beside how far it got. */}
                  <HandlingChips project={project.handling} />
                </td>
                <td>
                  <span className="ex-title-line">
                    {project.kind ?? "project"}
                    <WorkShapeTag shape={project.workShape} />
                  </span>
                </td>
                {showBudget ? (
                  <>
                    {/* Committed figures only; "-" is not-captured, never zero.
                      A rate prints in the client's own unit ("$1,500/mo"). */}
                    <td className="rc-num">{formatBudget(project.budget)}</td>
                    <td
                      className="rc-num"
                      title="The delivery window the client asked for, or the engagement's shape and start"
                    >
                      {formatTimeline(project.timeline)}
                    </td>
                  </>
                ) : null}
                <td>
                  <AlertCell count={project.alertCount} reasons={project.alertReasons} />
                </td>
                <td className="rc-num">{project.messages.toLocaleString()}</td>
                {showFiles ? (
                  <td className="rc-num">{project.files?.toLocaleString() ?? "-"}</td>
                ) : null}
                <td>{formatDateTime(project.updatedAt)}</td>
                <td>
                  <Link
                    to="/explorer/projects/$projectId"
                    params={{ projectId: project.id }}
                    className="ex-row-link"
                    aria-label={`Open ${projectTitle(project)}`}
                  >
                    <ArrowRightIcon size={16} />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <ShowMoreRow remaining={paged.remaining} onClick={paged.showMore} />
      </div>
    </>
  );
}

/** The shared page body. Both modes normalize into these props. */
function UserDetailView({
  user,
  identity,
  projects,
}: {
  user: DetailUser;
  identity: Record<string, unknown> | null;
  projects: OwnedProject[];
}) {
  return (
    <div className="ad-page">
      <header className="ex-detail__head">
        <BackLink to="/explorer" label="Back to Explorer" />
        <div className="ex-detail__title-row">
          <div>
            <h1 className="ad-page__title">{user.displayName || user.email}</h1>
            <p className="ad-page__sub">
              {user.displayName ? user.email : "Person"} <BlockedBadge blocked={user.blocked} />{" "}
              <ModelTierTag tier={user.modelTier} country={user.originCountry} />
            </p>
          </div>
          <div className="ex-detail__head-actions">
            {/* Straight into the conversation with this client on Fiverr, so an
              operator who has just read the thread can answer where the client
              actually is. An ordinary outbound link: it leaves the console, so
              it opens in a new tab and carries `noreferrer`. Absent entirely
              when we never learned their handle. */}
            {user.fiverrInboxUrl ? (
              <a
                className="ex-inbox-link"
                href={user.fiverrInboxUrl}
                target="_blank"
                rel="noreferrer"
                title="Open this client's Fiverr inbox conversation in a new tab"
              >
                Fiverr inbox
              </a>
            ) : null}
            {/* Renders nothing without the `user_administration` grant. */}
            <UserAdminActions
              user={{ id: user.id, email: user.email, blocked: user.blocked }}
              size="md"
            />
          </div>
        </div>
      </header>

      <div className="ex-detail__grid">
        <Card padding="lg">
          <h2 className="ad-card__title">Identity</h2>
          <IdentityFacts identity={identity} />
        </Card>

        {user.agentNotes ? (
          <Card padding="lg">
            <h2 className="ad-card__title">Agent notes</h2>
            <p className="ex-notes">{user.agentNotes}</p>
          </Card>
        ) : null}
      </div>

      <section className="ad-section">
        <h2 className="ad-section__title">
          Projects <span className="ex-count">{projects.length}</span>
        </h2>
        <OwnedProjects projects={projects} />
      </section>
    </div>
  );
}

function FullUserView({ id }: { id: string }) {
  const detail = useQuery(explorerUserQuery(id));
  const projects = useMemo<OwnedProject[]>(
    () =>
      (detail.data?.projects ?? []).map((p) => ({
        id: p.id,
        name: p.name,
        summary: p.summary,
        parentProjectId: p.parentProjectId,
        kind: p.kind,
        stage: p.stage,
        workShape: p.workShape ?? null,
        budget: p.budget,
        timeline: {
          deliveryMaxDays: p.deliveryMaxDays,
          deliveryMinDays: p.deliveryMinDays ?? null,
          startDate: p.startDate ?? null,
          durationKind: p.durationKind ?? null,
          durationMonths: p.durationMonths ?? null,
          durationMonthsMin: p.durationMonthsMin ?? null,
        },
        messages: p.counts.messages,
        files: p.counts.files ?? null,
        updatedAt: p.updatedAt,
        alertCount: p.openFlagCount,
        alertReasons: p.openFlagReasons,
        sellerIntent: p.sellerIntent ?? false,
        handling: p,
        starred: p.starred ?? false,
      })),
    [detail.data],
  );

  if (detail.isPending) {
    return (
      <div className="ad-page">
        <LoadingState label="Loading person…" />
      </div>
    );
  }
  if (detail.isError) {
    return (
      <div className="ad-page">
        <AreaBoundary area="explorer" error={detail.error} onRetry={() => void detail.refetch()} />
      </div>
    );
  }

  const { user, identity } = detail.data;
  return (
    <UserDetailView
      user={{
        id: user.id,
        email: user.email,
        displayName: user.displayName,
        agentNotes: user.agentNotes,
        blocked: user.blocked,
        modelTier: user.modelTier ?? null,
        originCountry: user.originCountry ?? null,
        fiverrInboxUrl: user.fiverrInboxUrl ?? null,
      }}
      identity={identity}
      projects={projects}
    />
  );
}

function SupportUserView({ id }: { id: string }) {
  const detail = useQuery(supportPersonQuery(id));
  const projects = useMemo<OwnedProject[]>(
    () =>
      (detail.data?.projects ?? []).map((p) => ({
        id: p.id,
        name: p.name,
        summary: p.summary ?? null,
        parentProjectId: null,
        kind: null,
        // The CS person payload carries none of the three; each is absent rather
        // than guessed at.
        stage: null,
        budget: null,
        timeline: null,
        messages: p.messageCount,
        // The CS payload carries no file count; the column is hidden entirely
        // rather than shown full of dashes.
        files: null,
        updatedAt: p.updatedAt ?? null,
        alertCount: p.openFlagCount ?? 0,
        alertReasons: p.openFlagReasons ?? [],
        sellerIntent: p.sellerIntent ?? false,
        // The CS payload carries no shape; null keeps the tag off.
        workShape: null,
        handling: {},
        starred: null,
      })),
    [detail.data],
  );

  if (detail.isPending) {
    return (
      <div className="ad-page">
        <LoadingState label="Loading person…" />
      </div>
    );
  }
  if (detail.isError) {
    return (
      <div className="ad-page">
        <AreaBoundary
          area="customer_support"
          error={detail.error}
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  const person = detail.data;
  return (
    <UserDetailView
      user={{
        id: person.id,
        email: person.email,
        displayName: person.displayName ?? null,
        // Cross-project agent notes are not in the CS payload.
        agentNotes: null,
        blocked: person.blocked,
      }}
      identity={null}
      projects={projects}
    />
  );
}

export function ExplorerUserPage({ id }: { id: string }) {
  const mode = useExplorerMode();
  return mode === "support" ? <SupportUserView id={id} /> : <FullUserView id={id} />;
}
